import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import AgentLayout from './components/AgentLayout';
import { useMarketResearchByIdQuery } from '@/queries/useMarketResearchByIdQuery';
import { useEffect, useState } from 'react';
import { MarketResearch } from '@/types/MarketResearch';
import { CircleArrowLeft, RefreshCw, Search } from 'lucide-react';
import { formatDateToString } from '@/utils/date';
import { TypographyH1, TypographyMuted } from '@/components/ui/typography';
import { useResearchedProductQuery } from '@/queries/useResearchedProductQuery';
import ButtonWithTooltip from '@/components/ButtonWithTooltip';
import { useBaseStore } from '@/store';
import { Store } from '@/store/type';
import AdmLayout from './components/AdmLayout';
import { useResearchCoverageQuery } from '@/queries/useResearchCoverageQuery';
import Button from '@/components/Button';
import LoadingScreen from '@/pages/LoadingScreen';
import ResearchCoverageDialog from './components/ResearchCoverageDialog';
import useDisclosure from '@/hooks/useDisclosure';
import { useGeolocationGuard } from '@/hooks/useGeolocationGuard';
import { MapPinOff, Navigation, Loader2 } from 'lucide-react';

const stateSelector = (state: Store) => ({
  profile: state.authSlice.state.profile,
});

interface TotalCoverage {
  CLIENTES_PESQUISA: number;
  CLIENTES_PESQUISADOS: number;
  TOTAL_PRODUTOS_PESQUISA: number;
  TOTAL_PRODUTOS_PESQUISADOS: number;
}

const MarketResearchDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('search');
  const [dataMarketResearch, setDataMarketResearch] =
    useState<MarketResearch>();
  const { profile } = useBaseStore(stateSelector);
  const { hasPermission, isChecking, errorMessage, requestLocation } =
    useGeolocationGuard();

  useEffect(() => {
    requestLocation();
  }, []);
  const [_, setTotalCoverage] = useState<TotalCoverage>({
    CLIENTES_PESQUISA: 0,
    CLIENTES_PESQUISADOS: 0,
    TOTAL_PRODUTOS_PESQUISA: 0,
    TOTAL_PRODUTOS_PESQUISADOS: 0,
  });

  if (!id) {
    navigate('/market-research');
  }

  const {
    isOpen: isOpenResearchCoverageDialog,
    onClose: onCloseResearchCoverageDialog,
  } = useDisclosure();

  const { data, isPending } = useMarketResearchByIdQuery(Number(id));
  const {
    data: dataResearchedProduct,
    refetch: refetchResearchedProduct,
    isFetching,
  } = useResearchedProductQuery(Number(id));
  const { data: dataResearchCoverage, refetch: refetchResearchCoverage } =
    useResearchCoverageQuery(Number(id));

  useEffect(() => {
    data && setDataMarketResearch(data[0]);
    console.log('data', data);
  }, [data]);

  useEffect(() => {
    if (dataResearchCoverage) {
      const resultResearch = dataResearchCoverage.reduce<TotalCoverage>(
        (acc, item) => {
          acc.CLIENTES_PESQUISA += item.CLIENTES_PESQUISA ?? 0;
          acc.CLIENTES_PESQUISADOS += item.CLIENTES_PESQUISADOS ?? 0;
          acc.TOTAL_PRODUTOS_PESQUISA += item.TOTAL_PRODUTOS_PESQUISA ?? 0;
          acc.TOTAL_PRODUTOS_PESQUISADOS +=
            item.TOTAL_PRODUTOS_PESQUISADOS ?? 0;
          return acc;
        },
        {
          CLIENTES_PESQUISA: 0,
          CLIENTES_PESQUISADOS: 0,
          TOTAL_PRODUTOS_PESQUISA: 0,
          TOTAL_PRODUTOS_PESQUISADOS: 0,
        },
      );
      setTotalCoverage(resultResearch);
    }
  }, [dataResearchCoverage]);

  if (isPending) return <LoadingScreen />;

  return (
    <div className="flex flex-col h-full overflow-auto p-4 bg-gray-50">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link className="hover:underline" to="/market-research">
                Planejamento de Pesquisa
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink>Detalhes</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>
              #{id} {dataMarketResearch && dataMarketResearch.NM_PESQUISA}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Fixed Location Alert Banner */}
      {!hasPermission && (
        <div className="my-3 bg-amber-50 border border-amber-300 rounded-xl p-3.5 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
              <MapPinOff size={22} />
            </div>
            <div>
              <h4 className="text-sm font-bold text-amber-900 leading-tight">
                Localização do Dispositivo Não Ativa
              </h4>
              <p className="text-xs text-amber-700 mt-0.5">
                {errorMessage ||
                  'A autorização de acesso à localização é obrigatória para iniciar a coleta e registrar dados das pesquisas.'}
              </p>
            </div>
          </div>

          <Button
            onClick={requestLocation}
            disabled={isChecking}
            className="w-full sm:w-auto text-xs h-9 px-4 bg-amber-600 hover:bg-amber-700 text-white font-bold gap-2 shrink-0"
          >
            {isChecking ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                Verificando...
              </>
            ) : (
              <>
                <Navigation size={15} />
                Ativar Localização
              </>
            )}
          </Button>
        </div>
      )}

      {dataMarketResearch && (
        <div className="flex justify-between my-4">
          <div className="flex flex-col gap-4 justify-between sm:items-center sm:flex-row">
            <div className="flex gap-3 items-center">
              <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-primary/10">
                <Search className="w-5 h-5 text-primary" />
              </div>
              <div>
                <TypographyH1 className="text-2xl font-semibold">
                  {dataMarketResearch.NM_PESQUISA}
                </TypographyH1>
                <TypographyMuted>
                  {dataMarketResearch.DS_REVENDA}
                </TypographyMuted>
                <TypographyMuted>
                  {formatDateToString(dataMarketResearch.SK_TEMPO_INICIO)} -{' '}
                  {formatDateToString(dataMarketResearch.SK_TEMPO_FIM)}
                </TypographyMuted>
              </div>
            </div>
          </div>
          <div className="flex gap-6">
            <Button
              className="md:flex hidden flex-col items-center text-xs"
              onClick={() => navigate(-1)}
            >
              <CircleArrowLeft size={18} />
              <TypographyMuted className="text-white">Voltar</TypographyMuted>
            </Button>
            <ButtonWithTooltip
              tooltipContent={<p className="text-white">Atualizar</p>}
              variant="ghost"
              isLoading={isFetching}
              onClick={() => {
                refetchResearchedProduct();
                refetchResearchCoverage();
              }}
              className="shadow  bg-white"
            >
              <RefreshCw size={18} />
            </ButtonWithTooltip>
          </div>
        </div>
      )}
      {dataResearchCoverage && (
        <ResearchCoverageDialog
          isOpen={isOpenResearchCoverageDialog}
          onClose={onCloseResearchCoverageDialog}
          researchCoverage={dataResearchCoverage}
        />
      )}

      {dataMarketResearch?.SK_PESQUISA && dataResearchedProduct && (
        <>
          {profile == 'REPRESENTANTE' && (
            <div>
              <AgentLayout
                marketResearch={dataMarketResearch}
                dataResearchedProducts={dataResearchedProduct}
              />
            </div>
          )}

          {(profile == 'ADMINISTRADOR' ||
            profile == 'ADMINISTRADOR_REVENDA') && (
            <div>
              <AdmLayout
                marketResearch={dataMarketResearch}
                dataResearchedProducts={dataResearchedProduct}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MarketResearchDetails;
